
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout.tsx';
import { dataService } from './services/dataService.ts';
import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import { ConfigProvider, useConfig } from './src/contexts/ConfigContext';
import { ThemeProvider, useTheme } from './src/contexts/ThemeContext';
import { TourProvider } from './src/contexts/TourContext';
import { TourSpotlight } from './components/TourSpotlight';
import { TourExitConfirmModal } from './components/TourExitConfirmModal';
import { Toaster } from 'react-hot-toast';
import { ROUTES, ROUTE_PERMISSIONS, LEGACY_ROUTE_REDIRECTS, LEGACY_PORTAL_ROUTE_REDIRECTS } from './src/routes';
import { canAccessRoute, isPureClient } from './src/lib/accessMatrix';
import { OPERATIONS_MODULE_ENABLED } from './src/lib/featureFlags';
import { useTranslation } from 'react-i18next';
import { registerServiceWorker } from './src/lib/pushNotifications';
import { TooltipLayer, JobProgressHost } from './src/ui';

// Lazy loading page components
const Home = lazy(() => import('./pages/Home'));
const Operations = lazy(() => import('./pages/Operations'));
const Accounting = lazy(() => import('./pages/Accounting'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Quote = lazy(() => import('./pages/Quote'));
const Commissions = lazy(() => import('./pages/Commissions'));
const Login = lazy(() => import('./src/pages/Login'));
const Settings = lazy(() => import('./pages/Settings'));
const Audit = lazy(() => import('./pages/Audit'));
const ClientManagement = lazy(() => import('./pages/ClientManagement'));
const SalesRepManagement = lazy(() => import('./pages/SalesRepManagement'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const BackupRestore = lazy(() => import('./pages/BackupRestore'));
const ClientPortal = lazy(() => import('./pages/ClientPortal/ClientPortal'));
const BillingReportView = lazy(() => import('./pages/BillingReport/BillingReportView'));
const Profile = lazy(() => import('./pages/Profile'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Mailbox = lazy(() => import('./pages/Mailbox'));
const Pickups = lazy(() => import('./pages/Pickups'));
const OperatorCases = lazy(() => import('./pages/OperatorCases'));
const Partners = lazy(() => import('./pages/Partners'));
const Agencies = lazy(() => import('./pages/Agencies'));
const WhatsappAgent = lazy(() => import('./pages/WhatsappAgent'));
const MyWhatsappChats = lazy(() => import('./pages/MyWhatsappChats'));
const CustomerFollowup = lazy(() => import('./pages/CustomerFollowup'));
const AgencyPortal = lazy(() => import('./pages/AgencyPortal'));

const QuoteApproval = lazy(() => import('./pages/QuoteApproval'));
const PublicTracking = lazy(() => import('./pages/PublicTracking'));
const DescargarApp = lazy(() => import('./pages/DescargarApp'));
const PublicQuoteRequest = lazy(() => import('./pages/PublicQuoteRequest'));
const ClientRegistration = lazy(() => import('./pages/ClientRegistration'));
const PoliticaPrivacidad = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TerminosServicio = lazy(() => import('./pages/legal/TermsOfService'));
const EliminacionDatos = lazy(() => import('./pages/legal/DataDeletion'));
const PoliticaCookies = lazy(() => import('./pages/legal/CookiePolicy'));

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-12 h-12 border-4 border-[#003B71]/20 border-t-[#003B71] rounded-full animate-spin"></div>
  </div>
);

/** Blocks access to a route if the user has none of the required roles. */
const RequireRole: React.FC<{ routeKey: keyof typeof ROUTE_PERMISSIONS; children: React.ReactNode }> = ({ routeKey, children }) => {
  const { user, effectiveRoles } = useAuth();

  if (routeKey !== 'home' && !canAccessRoute(effectiveRoles, routeKey)) {
    if (isPureClient(effectiveRoles)) return <Navigate to={ROUTES.clientPortal} replace />;
    return <Navigate to={ROUTES.home} replace />;
  }

  return <>{children}</>;
};

/** Redirects legacy Spanish URL paths to canonical English routes. */
const LegacyRouteRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { pathname, search, hash } = location;

  const exactTarget = LEGACY_ROUTE_REDIRECTS[pathname];
  if (exactTarget) {
    return <Navigate to={`${exactTarget}${search}${hash}`} replace />;
  }

  for (const [legacyPath, newPath] of Object.entries(LEGACY_ROUTE_REDIRECTS)) {
    const prefix = `${legacyPath}/`;
    if (!pathname.startsWith(prefix)) continue;

    const remainder = pathname.slice(prefix.length);
    const [firstSegment, ...restSegments] = remainder.split('/');
    const rest = restSegments.length > 0 ? `/${restSegments.join('/')}` : '';

    if (legacyPath === '/portal_clientes') {
      const mappedSegment = LEGACY_PORTAL_ROUTE_REDIRECTS[firstSegment] ?? firstSegment;
      return <Navigate to={`${newPath}/${mappedSegment}${rest}${search}${hash}`} replace />;
    }

    return <Navigate to={`${newPath}/${remainder}${search}${hash}`} replace />;
  }

  return <>{children}</>;
};

const AppShell: React.FC = () => {
  const { isAuthenticated, isCheckingSession } = useAuth();
  const { config } = useConfig();
  const { t } = useTranslation();
  const location = useLocation();

  const [isDbReady, setIsDbReady] = useState(false);
  const [connError, setConnError] = useState<string | null>(null);

  useEffect(() => {
    if (config.appName) {
      document.title = config.appName;
    }
  }, [config.appName]);

  // Service worker registration (Phase 4, push notifications) -- harmless; does not ask for
  // notification permission yet; this only triggers if the user explicitly activates push in Profile.tsx.
  useEffect(() => {
    registerServiceWorker();
  }, []);

  useEffect(() => {
    // Wait for AuthContext to confirm with the server whether the httpOnly session cookie
    // is still valid (there is no longer a token in localStorage to check synchronously) --
    // without this gate, a truly logged-in user would see a login-screen flash on every
    // reload while that confirmation is in flight.
    if (isCheckingSession) return;

    if (isAuthenticated) {
      const initDb = async () => {
        try {
          // Small delay to allow server to fully initialize
          await new Promise(resolve => setTimeout(resolve, 500));
          await dataService.init();
          setIsDbReady(true);
        } catch (err) {
          console.error(err);
          setConnError('failed');
        }
      };

      initDb();

      // Real-time updates listener -- native EventSource sends the httpOnly session cookie
      // on its own (same origin), so there is no need to pass the token as a query param
      // (keeps it out of access logs / browser history -- Security hardening (27/07)).
      const eventSource = new EventSource('/api/events');
      eventSource.onmessage = (event) => {
        // Structured payload (06/08, explicit requirement: real time across the app) -- previously
        // it was the literal string "update" with no hint of what changed, so every listening
        // page refetched everything with no way to filter. Now re-emitted with `detail` so
        // useDataRefresh can filter by entity and not overwrite an in-progress edit.
        try {
          const payload = JSON.parse(event.data);
          window.dispatchEvent(new CustomEvent('logistrade_refresh_data', { detail: payload }));
        } catch (e) {
          // Compatibility with the old format in case a client keeps a tab open during a
          // deploy -- ignored; no pages depend on the plain string anymore.
        }
      };
      // SSE connection status, consumed by useSSEConnectionStatus (notification bell) --
      // native EventSource retries on its own; this only reports visually whether the
      // real-time channel is down right now.
      eventSource.onopen = () => window.dispatchEvent(new CustomEvent('logistrade_sse_status', { detail: { connected: true } }));
      eventSource.onerror = () => window.dispatchEvent(new CustomEvent('logistrade_sse_status', { detail: { connected: false } }));
      return () => eventSource.close();
    } else {
      setIsDbReady(true); // Ready to show login
    }
  }, [isAuthenticated, isCheckingSession]);

  if (connError) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md text-center border-t-8 border-red-600">
          <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-black text-gray-800 mb-4 uppercase text-balance">{t('app.connectionErrorTitle')}</h1>
          <p className="text-gray-600 mb-6 font-medium">{t('app.dbConnectionError')}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:bg-red-700 transition-all w-full active:scale-95"
          >
            {t('app.retryConnection')}
          </button>
        </div>
      </div>
    );
  }

  if (!isDbReady) {
    return (
      <div className="min-h-screen bg-[#003B71] flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-bold tracking-widest animate-pulse text-xs">{t('app.connecting', { name: config.appName })}</p>
        </div>
      </div>
    );
  }

  // Public route, no auth: quote approval via email link
  if (location.pathname.startsWith('/quote-approval/')) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <QuoteApproval />
      </Suspense>
    );
  }

  // Public route, no auth: tracking by shipment number (task §33.23 point 5)
  if (location.pathname === '/tracking' || location.pathname.startsWith('/tracking/')) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <PublicTracking />
      </Suspense>
    );
  }

  // Public route, no auth: instalación de la app móvil (APK de Android y alternativa para iPhone).
  if (location.pathname === '/descargar') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <DescargarApp />
      </Suspense>
    );
  }

  // Public route, no auth: quote request from an anonymous user -- creates nothing in
  // `quotes`, only emails Operations (see POST /api/public/quote-requests). From there
  // Corley runs the existing flow (create user, real quote, client approval).
  if (location.pathname === '/cotiza') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <PublicQuoteRequest />
      </Suspense>
    );
  }

  // Public route, no auth: client self-registration (POST /api/public/register).
  if (location.pathname === '/registro') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <ClientRegistration />
      </Suspense>
    );
  }

  // Public routes, no auth: legal pages required by Meta (WhatsApp Business / Threads
  // App Dashboard) -- privacy policy, terms, and data deletion.
  if (location.pathname === '/privacidad') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <PoliticaPrivacidad />
      </Suspense>
    );
  }
  if (location.pathname === '/terminos') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <TerminosServicio />
      </Suspense>
    );
  }
  if (location.pathname === '/eliminacion-datos') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <EliminacionDatos />
      </Suspense>
    );
  }
  if (location.pathname === '/cookies') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <PoliticaCookies />
      </Suspense>
    );
  }

  // Same race the AppShell effect above already guards against: on every mount (StrictMode's
  // double-invoke in dev makes it worse), this component renders once, synchronously, before
  // AuthContext's effect has had a chance to read the cached user from localStorage or confirm
  // the session with the server. Without this gate, that first render sees `isAuthenticated`
  // still false and mounts the (lazily-loaded) Login page for real -- not just a flash -- and
  // by the time the session check resolves, the interactive Login screen has already stolen
  // focus/state, so the fix only shows up on the NEXT reload instead of the current one.
  if (isCheckingSession) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Login />
      </Suspense>
    );
  }

  return (
    <Layout>
      <div className="animate-fadeIn">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path={ROUTES.home} element={<Home />} />
            <Route
              path={`${ROUTES.clientPortal}/*`}
              element={<RequireRole routeKey="clientPortal"><div data-tour-id="page-root"><ClientPortal /></div></RequireRole>}
            />
            <Route
              path={ROUTES.operations}
              element={
                OPERATIONS_MODULE_ENABLED ? (
                  <RequireRole routeKey="operations">
                    <div data-tour-id="page-root"><Operations /></div>
                  </RequireRole>
                ) : (
                  <Navigate to={ROUTES.home} replace />
                )
              }
            />
            <Route
              path={ROUTES.accounting}
              element={<RequireRole routeKey="accounting"><div data-tour-id="page-root"><Accounting /></div></RequireRole>}
            />
            <Route
              path={ROUTES.dashboard}
              element={<RequireRole routeKey="dashboard"><div data-tour-id="page-root"><Dashboard /></div></RequireRole>}
            />
            <Route
              path={ROUTES.quote}
              element={<RequireRole routeKey="quote"><div data-tour-id="page-root"><Quote /></div></RequireRole>}
            />
            <Route
              path={ROUTES.commissions}
              element={<RequireRole routeKey="commissions"><div data-tour-id="page-root"><Commissions /></div></RequireRole>}
            />
            <Route
              path={`${ROUTES.billingReport}/*`}
              element={<RequireRole routeKey="billingReport"><div data-tour-id="page-root"><BillingReportView /></div></RequireRole>}
            />
            <Route
              path={ROUTES.settings}
              element={<RequireRole routeKey="settings"><div data-tour-id="page-root"><Settings /></div></RequireRole>}
            />
            <Route
              path={ROUTES.audit}
              element={<RequireRole routeKey="audit"><div data-tour-id="page-root"><Audit /></div></RequireRole>}
            />
            <Route
              path={ROUTES.mailbox}
              element={<RequireRole routeKey="mailbox"><div data-tour-id="page-root"><Mailbox /></div></RequireRole>}
            />
            <Route
              path={ROUTES.pickups}
              element={<RequireRole routeKey="pickups"><div data-tour-id="page-root"><Pickups /></div></RequireRole>}
            />
            <Route
              path={ROUTES.cases}
              element={<RequireRole routeKey="cases"><div data-tour-id="page-root"><OperatorCases /></div></RequireRole>}
            />
            <Route
              path={ROUTES.partners}
              element={<RequireRole routeKey="partners"><div data-tour-id="page-root"><Partners /></div></RequireRole>}
            />
            <Route
              path={ROUTES.agencies}
              element={<RequireRole routeKey="agencies"><div data-tour-id="page-root"><Agencies /></div></RequireRole>}
            />
            <Route
              path={ROUTES.whatsappAgent}
              element={<RequireRole routeKey="whatsappAgent"><div data-tour-id="page-root"><WhatsappAgent /></div></RequireRole>}
            />
            <Route
              path={ROUTES.myWhatsappChats}
              element={<RequireRole routeKey="myWhatsappChats"><div data-tour-id="page-root"><MyWhatsappChats /></div></RequireRole>}
            />
            <Route
              path={ROUTES.customerFollowup}
              element={<RequireRole routeKey="customerFollowup"><div data-tour-id="page-root"><CustomerFollowup /></div></RequireRole>}
            />
            <Route
              path={ROUTES.agencyPortal}
              element={<RequireRole routeKey="agencyPortal"><div data-tour-id="page-root"><AgencyPortal /></div></RequireRole>}
            />
            <Route
              path={ROUTES.clients}
              element={<RequireRole routeKey="clients"><div data-tour-id="page-root"><ClientManagement /></div></RequireRole>}
            />
            <Route
              path={ROUTES.salesReps}
              element={<RequireRole routeKey="salesReps"><div data-tour-id="page-root"><SalesRepManagement /></div></RequireRole>}
            />
            <Route
              path={ROUTES.users}
              element={<RequireRole routeKey="users"><UserManagement /></RequireRole>}
            />
            <Route
              path={ROUTES.backup}
              element={<RequireRole routeKey="backup"><BackupRestore /></RequireRole>}
            />
            <Route
              path={ROUTES.profile}
              element={<RequireRole routeKey="profile"><Profile /></RequireRole>}
            />
            <Route
              path={ROUTES.notifications}
              element={<RequireRole routeKey="notifications"><Notifications /></RequireRole>}
            />
            <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
          </Routes>
        </Suspense>
      </div>
    </Layout>
  );
};

/**
 * Theme-aware toasts (11/08, bug reported with screenshot).
 *
 * `react-hot-toast` paints the container with a fixed WHITE background, while this
 * project's toast bodies use `dark:` classes (e.g. `dark:text-slate-200`). In dark mode
 * that meant light text on white: the cancel-pickup modal was nearly unreadable.
 * Fixed once on the container instead of patching every toast.
 *
 * Lives in a separate component because `useTheme()` must be INSIDE `ThemeProvider`.
 */
const ToasterConTema: React.FC = () => {
  const { theme } = useTheme();
  const oscuro = theme === 'dark';
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        style: {
          background: oscuro ? '#1e293b' : '#ffffff',
          color: oscuro ? '#f1f5f9' : '#0f172a',
          border: `1px solid ${oscuro ? '#334155' : '#e2e8f0'}`,
        },
        // Success/error icons carry their own brand color; only the circle's background is
        // adjusted so no white halo shows on the dark toast.
        success: { iconTheme: { primary: '#10b981', secondary: oscuro ? '#1e293b' : '#ffffff' } },
        error: { iconTheme: { primary: '#f43f5e', secondary: oscuro ? '#1e293b' : '#ffffff' } },
      }}
    />
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ConfigProvider>
            <TourProvider>
              <ToasterConTema />
              <JobProgressHost />
              <LegacyRouteRedirect>
                <AppShell />
              </LegacyRouteRedirect>
              <TooltipLayer />
              <TourSpotlight />
              <TourExitConfirmModal />
            </TourProvider>
          </ConfigProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
